import {
  contractLineService,
  movementReasonService,
  orderLineService,
  orderService,
  productService,
  stockQuantityLocationService,
  stockQuantityService,
  tenantSettingsService,
  warehouseService,
  locationService
} from 'services/api';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { DatePickerAdapter } from 'components/form/DatePickerAdapter';
import { IdentityField } from 'components/form/IdentityField';
import { IOption, IOptionStockQuantity, ITenantSettings } from 'interfaces';
import { required } from 'helpers/validators';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactFinalForm from 'components/form/ReactFinalForm';
import dayjs from 'dayjs';
import { Alert } from '@mui/material';
import { CheckboxAdapter } from 'components/form/CheckboxAdapter';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { DateTimePickerAdapter } from 'components/form/DateTimePickerAdapter';

interface ManualMovementProps {
  onSubmit: any;
}

interface ContractLineOption {
  id: number;
  code: string;
  unit_price: number | string;
}

function ManualMovement(props: ManualMovementProps) {
  const { onSubmit } = props;
  const { t } = useTranslation();

  const defaultWarehouse = null;
  // const defaultWarehouse = useAppSelector((state) => state.auth.warehouse);
  // useEffect(() => {
  //   setWarehouse({ code: defaultWarehouse, id: 0 });
  // }, [defaultWarehouse]);

  const [contractLines, setContractLines] = useState<ContractLineOption[]>([]);
  const [batches, setBatches] = useState<IOptionStockQuantity[]>([]);
  const [locations, setLocations] = useState<IOption[]>([]);
  const [movementReasons, setMovementReasons] = useState<IOption[]>([]);
  const [products, setProducts] = useState<IOption[]>([]);
  const [warehouses, setWarehouses] = useState<IOption[]>([]);

  const [expiration, setExpiration] = useState<Date>();
  const [orders, setOrders] = useState<IOption[]>([]);
  const [order, setOrder] = useState<IOption | null>(null);
  const [product, setProduct] = useState<IOption | null>(null);
  const [tenantSettings, setTenantSettings] = useState<ITenantSettings>();
  const [type, setType] = useState<IOption | null>(null);
  const [warehouse, setWarehouse] = useState<IOption | null>(null);

  // const [orderNotes, setOrderNotes] = useState('');
  const [purchaseReqNotes, setPurchaseReqNotes] = useState('');

  const [refresh, setRefresh] = useState(false); // Per forzare il reset del componente AutocompleteAdapterProduct
  const tenant = process.env.REACT_APP_TENANT;

  // 1. EFFETTO INIZIALE: caricamento base
  useEffect(() => {
    tenantSettingsService
      .get(1)
      .then(setTenantSettings)
      .catch(() => setTenantSettings(undefined));
    warehouseService
      .getAllBaseInformation()
      .then((res) => {
        if (res && Array.isArray(res)) {
          setWarehouses(res);
        } else {
          setWarehouses([]);
        }
      })
      .catch(() => setWarehouses([]));
    movementReasonService
      .getAllPaginated()
      .then((res) => setMovementReasons(res?.results || []))
      .catch(() => setMovementReasons([]));
    productService
      .getAllBaseInformation()
      .then((res) => {
        if (res && Array.isArray(res)) {
          setProducts(res);
        } else {
          setProducts([]);
        }
      })
      .catch(() => setProducts([]));
  }, []);

  // 2. Caricamento prodotti "base" (solo all'avvio)
  useEffect(() => {
    if (!warehouse?.id) return;

    const params: any = {
      warehouse_ids: warehouse.id // Usa warehouse_id invece di warehouse_ids per escludere automaticamente i prodotti dismessi
    };

    if (order?.id) {
      params.order_id = order.id; // Filtro prodotti legati all'ordine
      params.has_order_line = true;
      setProducts([]); // resetta i prodotti per evitare confusione
    }

    productService
      .getAllBaseInformation(params)
      .then((res) => {
        if (res && Array.isArray(res)) {
          setProducts(res);
        } else {
          setProducts([]);
        }
        // Mantieni il prodotto selezionato se ancora valido
        setProduct(null);
        setRefresh(!refresh);
      })
      .catch(() => {
        setProducts([]);
        setProduct(null);
        setRefresh(!refresh);
      });
  }, [warehouse?.id, order?.id]);

  // 3️⃣ Caricamento ordini "aperti" (solo all'avvio o quando cambia direzione del movimento)
  useEffect(() => {
    if (!type?.id) return;

    const params: any = { status: 1 };
    // PER MESTRE: solo per ordini in uno specifico magazzino
    if (tenant !== 'ROMA2') {
      if (warehouse?.id) {
        params.warehouse = warehouse.code;
      }
    }
    orderService
      .openOrders(1000, 0, params)
      .then((res) => {
        if (res?.results && Array.isArray(res.results)) {
          setOrders(
            res.results.map((v) => ({
              id: v.id,
              code: v.ref,
              description: v.description
            }))
          );
        } else {
          setOrders([]);
        }
      })
      .catch(() => setOrders([]));
  }, [type?.id, warehouse?.id]);

  // 4️⃣ EFFETTO: lazy loading delle piazze (solo se serve)
  useEffect(() => {
    if (!warehouse?.id) return;
    if (!type || type['direction'] !== 'IN') return; // esempio: carica piazze solo in ingresso
    if (locations.length > 0) return; // evita chiamate duplicate

    locationService
      .getAllBaseInformation({ warehouse_id: warehouse.id })
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [warehouse?.id, type?.id]);

  // 5️⃣ EFFETTO: caricamento batches solo se tutti i prerequisiti sono presenti
  useEffect(() => {
    if (!(type && type['direction'] === 'OUT' && product?.id && warehouse?.id)) {
      setBatches([]);
      return;
    }

    const params = {
      product__id: product.id,
      warehouse__code: warehouse.code,
      quantity__gt: 0
    };

    stockQuantityService
      .getUnloadStocks(1000, 0, params)
      .then((res) => {
        if (res?.results && Array.isArray(res.results)) {
          const firstBatchData = res.results
            .filter((v) => v.free_quantity > 0)
            .map((v) => {
              let description = v.product_batch;
              if (v.expiration_date) description += `; Scad: ${v.expiration_date}`;
              description += `; Qty: ${v.free_quantity}`;
              return {
                id: crypto.randomUUID(),
                code: description,
                product_batch: v.product_batch,
                expiration_date: v.expiration_date
              };
            });

          setBatches(firstBatchData);

          // seconda chiamata API per location
          return stockQuantityLocationService.getUnloadStockLocation(1000, 0, params);
        } else {
          setBatches([]);
          return Promise.resolve({ results: [] });
        }
      })
      .then((res) => {
        if (res?.results && Array.isArray(res.results)) {
          const secondBatchData = res.results.map((v) => {
            let description = v.product_batch;
            if (v.expiration_date) description += `; Scad: ${v.expiration_date}`;
            description += `; Qty: ${v.quantity}; ${v.location__code}`;
            return {
              id: crypto.randomUUID(),
              code: description,
              product_batch: v.product_batch,
              expiration_date: v.expiration_date,
              location: v.location__code,
              qty: v.quantity
            };
          });

          setBatches((prev) => [...prev, ...secondBatchData]);
        }
      })
      .catch(() => setBatches([]));
  }, [type?.id, product?.id, warehouse?.id]);

  // ✅ 6️⃣ EFFETTO: caricamento contract lines (solo quando ci sono sia magazzino che prodotto)
  useEffect(() => {
    if (!(product?.id && warehouse?.id)) {
      setContractLines([]);
      return;
    }

    const params = {
      product_id: product.id,
      warehouse: warehouse.id
    };

    contractLineService
      .getAllBaseInformation(params)
      .then((res) => {
        if (res && Array.isArray(res)) {
          setContractLines(
            res.map((cl) => ({
              id: cl.id,
              code: `${cl.code} - ${cl.supplier}`,
              unit_price: cl.unit_price
            }))
          );
        } else {
          setContractLines([]);
        }
      })
      .catch(() => setContractLines([]));
  }, [product?.id, warehouse?.id]);

  // ✅ 7️⃣ EFFETTO: caricamento note da richiesta di acquisto
  useEffect(() => {
    if (order?.id && product?.id && warehouse?.id) {
      orderLineService
        .getAllPaginated(1, 0, {
          ref: order.code,
          product: product.code,
          warehouse: warehouse.code,
          status: 'PENDING'
        })
        .then((res) => {
          if (res?.results?.length > 0) {
            setPurchaseReqNotes(res.results[0]?.notes ?? '');
          } else {
            setPurchaseReqNotes('');
          }
        })
        .catch(() => setPurchaseReqNotes(''));
    } else {
      setPurchaseReqNotes('');
    }
  }, [order?.id, product?.id, warehouse?.id]);

  const submitForm = (values) => {
    productService.get(product.id).then((res) => {
      const selectedBatch: IOptionStockQuantity = batches.find(
        (batch) => batch.id === values.product_batch
      );
      // Genero il "BLOB MOVIMENTO" contenenente i dati da inviare al componente padre
      const movement = {
        ...values,
        description: res.description,
        product_batch: selectedBatch
          ? selectedBatch.product_batch
          : values.product_batch || 'NO_BATCH',
        warehouse: warehouse?.code || '',
        unit_price: contractLines.length === 1 ? contractLines[0].unit_price || 0 : undefined,
        contract_line: contractLines.length === 1 ? contractLines[0].id : undefined,
        note_location:
          type['direction'] === 'IN'
            ? values.notes_location || ''
            : selectedBatch?.location || values.location || ''
      };
      const results = {
        movement: movement,
        batches: batches
      };
      onSubmit(results);
    });
  };

  return (
    <ReactFinalForm onSubmit={submitForm}>
      <div className="flex flex-wrap my-4">
        {tenantSettings &&
          expiration &&
          tenantSettings.days_before_expiration_alert &&
          dayjs(expiration).diff(dayjs(), 'days') <=
            tenantSettings.days_before_expiration_alert && (
            <Alert severity="error" className="w-full">
              {t(`alerts.product-close-to-expiration`)}
            </Alert>
          )}
        {purchaseReqNotes && (
          <Alert severity="info" className="w-full mb-2">
            {purchaseReqNotes}
          </Alert>
        )}
        {!defaultWarehouse && (
          <div className="w-full my-2">
            <IdentityField
              name="warehouse"
              component={AutocompleteAdapter}
              options={warehouses}
              label={t('global.warehouse')}
              type="text"
              optionValue={'code'}
              setValue={setWarehouse}
              validate={required}
            />
          </div>
        )}
        {type && type['direction'] === 'IN' && !type['order_independent'] ? (
          <>
            <div className="w-full my-2">
              <IdentityField
                name="order_code"
                component={AutocompleteAdapter}
                options={orders}
                label={t('entities.movement.order_code')}
                type="text"
                optionValue={'code'}
                optionLabel={'description'}
                setValue={setOrder}
                autoSelect={false}
              />
            </div>
          </>
        ) : null}
        <div className="w-full my-2">
          <IdentityField
            key={refresh}
            name="product"
            label={t('global.product')}
            component={AutocompleteAdapter}
            options={products}
            type="text"
            optionValue={'code'}
            optionLabel={'description'}
            setValue={setProduct}
            validate={required}
          />
        </div>
        <div className="w-full md:w-1/2 my-2 md:pr-2">
          <IdentityField
            name="movement_type"
            label={t('entities.movement.type')}
            component={AutocompleteAdapter}
            options={movementReasons}
            optionLabel="description"
            optionValue="code"
            type="text"
            setValue={setType}
            validate={required}
          />
        </div>
        <div className="w-full md:w-1/2 my-2 md:pl-2">
          <IdentityField
            name="moved_quantity"
            label={t('entities.movement.moved_quantity')}
            component={TextFieldAdapter}
            type="number"
            validate={required}
          />
        </div>
        {contractLines?.length > 1 && (
          <div className="w-full my-2">
            <IdentityField
              name="contract_line"
              label={t('global.contract')}
              component={AutocompleteAdapter}
              options={contractLines}
              type="text"
              validate={required}
            />
          </div>
        )}
        {type && type['direction'] === 'OUT' ? (
          <div className="w-full">
            <IdentityField
              key={batches.length}
              name="product_batch"
              label={t('entities.movement.product_batch')}
              component={AutocompleteAdapter}
              options={batches}
              type="text"
              validate={required}
              required
            />
          </div>
        ) : (
          <div className="w-full">
            <IdentityField
              name="product_batch"
              label={t('entities.movement.product_batch')}
              component={TextFieldAdapter}
              type="text"
            />
          </div>
        )}
        {type && type['direction'] === 'IN' ? (
          <>
            <div className="w-full my-2">
              <IdentityField
                name="expiration_date"
                component={DatePickerAdapter}
                label={t('entities.movement.expiration_date')}
                setValue={setExpiration}
                type="date"
              />
            </div>
            <div className="w-full md:w-1/2 my-2 md:pr-2">
              <IdentityField
                name="transport_document"
                component={TextFieldAdapter}
                label={t('entities.movement.transport_document')}
                type="text"
              />
            </div>
            <div className="w-full md:w-1/2 my-2 md:pl-2">
              <IdentityField
                name="transport_document_date"
                component={DatePickerAdapter}
                label={t('entities.movement.transport_document_date')}
                type="date"
                noFuture={true}
              />
            </div>
            {locations.length > 0 && (
              <div className="w-full">
                <IdentityField
                  name="notes_location"
                  component={AutocompleteAdapter}
                  label={t('entities.movement.notes_location')}
                  options={locations}
                  type="text"
                  optionValue={'code'}
                  autoSelect={false}
                  required={false}
                />
              </div>
            )}
          </>
        ) : null}
        <div className="w-full my-2">
          <IdentityField
            name="notes"
            component={TextFieldAdapter}
            label={t('global.notes')}
            type="text"
          />
        </div>
        <IsTenantAdministrator>
          <div className="w-full my-2">
            <IdentityField
              name="timestamp"
              component={DateTimePickerAdapter}
              label={t(`entities.movement.timestamp`)}
              type="date"
            />
          </div>
        </IsTenantAdministrator>

        {type && type['direction'] === 'IN' ? (
          <>
            <div className="w-full my-2">
              <IdentityField
                name="print_labels"
                component={CheckboxAdapter}
                label={t(`pages.movements.print_labels`)}
                type="checkbox"
              />
            </div>
            <div className="w-full my-2">
              <IdentityField
                name="print_barcodes"
                component={CheckboxAdapter}
                label={t(`pages.movements.print_barcodes`)}
                type="checkbox"
              />
            </div>
          </>
        ) : null}
      </div>
    </ReactFinalForm>
  );
}

export default ManualMovement;
